// src/components/CreatePurchaseOrders.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import "../styles/CreatePurchaseOrders.css";
import Select from "react-select";
import { customSelectStyles } from "../styles/reactSelectStyles.js";
import { FaCalendarAlt, FaClock } from "react-icons/fa";
import { useTranslation } from "react-i18next";

/**
 * Ajusta estes endpoints conforme as tuas rotas reais:
 * - POST: criar purchase order
 * - GET: buscar next purchaseOrderNumber (opcional)
 */
const CREATE_PO_ENDPOINT = "/purchaseorders";
const NEXT_NUMBER_ENDPOINT = "/purchaseorders/next-number"; // opcional (se existir)

const NEXT_PO_CACHE_KEY = "po_next_number_v1";

function readNextPoCache() {
  try {
    const raw = window.sessionStorage.getItem(NEXT_PO_CACHE_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    const n = parsed?.n;
    return n ? String(n) : "";
  } catch {
    return "";
  }
}

function writeNextPoCache(n) {
  try {
    window.sessionStorage.setItem(
      NEXT_PO_CACHE_KEY,
      JSON.stringify({ ts: Date.now(), n })
    );
  } catch {
    // ignore
  }
}

const isValidISO2 = (v) => /^[A-Z]{2}$/i.test((v || "").trim());

const toDateInput = (d) => {
  if (!d) return "";
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
};

const addDays = (yyyyMmDd, days) => {
  if (!yyyyMmDd) return "";
  const dt = new Date(`${yyyyMmDd}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return "";
  dt.setDate(dt.getDate() + Number(days || 0));
  return dt.toISOString().slice(0, 10);
};

const diffDays = (from, to) => {
  if (!from || !to) return null;
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const ms = b.getTime() - a.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

const normalizeHour = (raw) => {
  if (raw === "") return "";
  const cleaned = raw.replace(/[^\d:]/g, "");
  if (/^\d{2}:\d{2}$/.test(cleaned)) return cleaned;
  const digits = cleaned.replace(/:/g, "");
  if (/^\d{4}$/.test(digits)) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  return cleaned;
};

const isValidHour = (v) => {
  if (!v) return true; // allow null/empty
  if (!/^\d{2}:\d{2}$/.test(v)) return false;
  const [hh, mm] = v.split(":").map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return false;
  return hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59;
};

const clampDecimal = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  const s = String(v).replace(",", ".");
  if (!/^\d*\.?\d{0,2}$/.test(s)) return s.replace(/[^\d.]/g, "");
  return s;
};

const openNativeDatePicker = (e) => {
  // Chrome/Edge suportam; noutros browsers simplesmente não faz nada (safe)
  try {
    if (e?.target?.showPicker) e.target.showPicker();
  } catch {
    // ignore
  }
};

const CreatePurchaseOrders = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("create_purchase_orders");

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [createdPoNumber, setCreatedPoNumber] = useState("");

  // Header-ish fields
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState("");
  const [status, setStatus] = useState(""); // ✅ obrigatório -> começa vazio

  // Relations (optional)
  const [transportsessionId, setTransportsessionId] = useState("");
  const [loadId, setLoadId] = useState("");

  // Core fields
  const [client, setClient] = useState("");

  // Optional general date/hour (exists in model)
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("");

  // Pickup/delivery (simple strings)
  const [pickup, setPickup] = useState("");
  const [delivery, setDelivery] = useState("");

  // Timeline pickup/delivery (REQUIRED)
  const [pickupDate, setPickupDate] = useState("");
  const [pickupHour, setPickupHour] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryHour, setDeliveryHour] = useState("");

  // Pricing / VAT (REQUIRED)
  const [price, setPrice] = useState("");
  const [price4subcontrated, setPrice4subcontrated] = useState("");
  const [vatRate, setVatRate] = useState("");
  const [issuerCountry, setIssuerCountry] = useState("PT");

  // Issue / Payment (REQUIRED)
  const [issueDate, setIssueDate] = useState(toDateInput(new Date()));
  const [paymentDueDate, setPaymentDueDate] = useState("");
  const [netDays, setNetDays] = useState(null); // ✅ obrigatório -> começa sem seleção
  const [netDaysCustom, setNetDaysCustom] = useState("");

  // Extra (lists) — ADVANCED (OPTIONAL)
  const [pickupCount, setPickupCount] = useState(0);
  const [deliveryCount, setDeliveryCount] = useState(0);
  const [pickupLocation, setPickupLocation] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState("");
  const [pickupListJson, setPickupListJson] = useState("[]");
  const [deliveryListJson, setDeliveryListJson] = useState("[]");

  const [showAdvanced, setShowAdvanced] = useState(false);

  // Validation visuals
  const [invalidFields, setInvalidFields] = useState({});
  const markInvalid = (key, isInvalid) =>
    setInvalidFields((prev) => {
      if (isInvalid) return { ...prev, [key]: true };
      if (!prev[key]) return prev;
      const clone = { ...prev };
      delete clone[key];
      return clone;
    });

  // Field-level error messages (like Invoices) — ONLY "X é/são obrigatório/s"
  const [fieldErrors, setFieldErrors] = useState({});

  const clearFieldError = (key) =>
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const clone = { ...prev };
      delete clone[key];
      return clone;
    });

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: "DRAFT", label: t("status.draft") },
      { value: "SENT", label: t("status.sent") },
      { value: "APPROVED", label: t("status.approved") },
      { value: "PAID", label: t("status.paid") },
      { value: "CANCELLED", label: t("status.cancelled") },
    ],
    [t]
  );

  const NET_DAYS_OPTIONS = useMemo(
    () => [
      { value: 0, label: t("netDays.0") },
      { value: 15, label: t("netDays.15") },
      { value: 30, label: t("netDays.30") },
      { value: 45, label: t("netDays.45") },
      { value: 60, label: t("netDays.60") },
      { value: 90, label: t("netDays.90") },
    ],
    [t]
  );

  const netDaysEffective = useMemo(() => {
    if (netDays === null || netDays === "") return null; // ✅ sem seleção
    const custom = Number(netDaysCustom);
    if (netDays === "CUSTOM" && Number.isFinite(custom) && custom >= 0) return custom;
    if (netDays === "CUSTOM") return null;
    return Number(netDays);
  }, [netDays, netDaysCustom]);

  const didFetchNextNumberRef = useRef(false);

  // Try fetch next number (optional route) — instant render + cache + background refresh
  useEffect(() => {
    // ✅ evita duplicar requests no dev (StrictMode)
    if (didFetchNextNumberRef.current) return;
    didFetchNextNumberRef.current = true;

    const ac = new AbortController();

    (async () => {
      try {
        const res = await axios.get(NEXT_NUMBER_ENDPOINT, { signal: ac.signal });

        const n = res?.data?.purchaseOrderNumber ?? res?.data?.next ?? null;
        if (n) {
          setPurchaseOrderNumber(String(n));
          writeNextPoCache(String(n));
        }
      } catch (e) {
        // ok se não existir endpoint / abort / etc.
      }
    })();

    return () => ac.abort();
  }, []);

  // Auto-calc paymentDueDate when issueDate/netDays changes
  useEffect(() => {
    if (!issueDate) return;
    if (netDaysEffective === null) return; // ✅ inclui netDays null/sem seleção
    setPaymentDueDate(addDays(issueDate, netDaysEffective));
  }, [issueDate, netDaysEffective]);

  const validate = () => {
    const nextInvalid = {};
    const nextErrors = {};

    // ✅ all messages equal, only field name changes
    const reqLabel = (label, plural = false) =>
      plural
        ? t("validation.required_plural", { field: label })
        : t("validation.required_singular", { field: label });

    const req = (key, value, fieldLabelKey, plural = false) => {
      const empty = !String(value ?? "").trim();
      if (empty) {
        nextInvalid[key] = true;
        nextErrors[key] = reqLabel(t(fieldLabelKey), plural);
        return false;
      }
      return true;
    };

    // REQUIRED
    req("client", client, "fields.client");

    // ✅ REQUIRED: status (dropdown)
    req("status", status, "fields.status");

    // ✅ REQUIRED: paymentTerms (dropdown)
    req("netDays", netDays, "fields.paymentTerms", true);

    // ✅ REQUIRED: se paymentTerms = CUSTOM -> netDaysCustom obrigatório
    if (netDays === "CUSTOM") {
      req("netDaysCustom", netDaysCustom, "fields.paymentTerms", true);
    }

    // issuerCountry required (ISO2) — if invalid, same required message (as requested)
    if (req("issuerCountry", issuerCountry, "fields.issuerCountry")) {
      if (!isValidISO2(issuerCountry)) {
        nextInvalid.issuerCountry = true;
        nextErrors.issuerCountry = reqLabel(t("fields.issuerCountry"));
      }
    }

    // vatRate required (0..100) — invalid -> same message
    if (req("vatRate", vatRate, "fields.vatRate")) {
      const n = Number(String(vatRate).replace(",", "."));
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        nextInvalid.vatRate = true;
        nextErrors.vatRate = reqLabel(t("fields.vatRate"));
      }
    }

    // price required (>=0) — invalid -> same message
    if (req("price", price, "fields.price")) {
      const n = Number(String(price).replace(",", "."));
      if (!Number.isFinite(n) || n < 0) {
        nextInvalid.price = true;
        nextErrors.price = reqLabel(t("fields.price"));
      }
    }

    // price4subcontrated required (>=0) — invalid -> same message
    if (req("price4subcontrated", price4subcontrated, "fields.price4subcontrated")) {
      const n = Number(String(price4subcontrated).replace(",", "."));
      if (!Number.isFinite(n) || n < 0) {
        nextInvalid.price4subcontrated = true;
        nextErrors.price4subcontrated = reqLabel(t("fields.price4subcontrated"));
      }
    }

    // issue/payment required
    req("issueDate", issueDate, "fields.issueDate");
    req("paymentDueDate", paymentDueDate, "fields.paymentDueDate");

    // PaymentTerms: NOT required. If CUSTOM and user typed something invalid -> same message
    if (netDays === "CUSTOM" && String(netDaysCustom ?? "").trim()) {
      const n = Number(netDaysCustom);
      if (!Number.isFinite(n) || n < 0) {
        nextInvalid.netDaysCustom = true;
        nextErrors.netDaysCustom = reqLabel(t("fields.paymentTerms"), true);
      }
    }

    // OPTIONAL: date/hour (only validate hour format if provided)
    if (String(hour ?? "").trim() && !isValidHour(hour)) {
      nextInvalid.hour = true;
      nextErrors.hour = reqLabel(t("fields.hourOptional"));
    }

    // REQUIRED: pickup/delivery timeline
    req("pickup", pickup, "fields.pickup");
    req("pickupDate", pickupDate, "fields.pickupDate");
    if (req("pickupHour", pickupHour, "fields.pickupHour")) {
      if (!isValidHour(pickupHour)) {
        nextInvalid.pickupHour = true;
        nextErrors.pickupHour = reqLabel(t("fields.pickupHour"));
      }
    }

    req("delivery", delivery, "fields.delivery");
    req("deliveryDate", deliveryDate, "fields.deliveryDate");
    if (req("deliveryHour", deliveryHour, "fields.deliveryHour")) {
      if (!isValidHour(deliveryHour)) {
        nextInvalid.deliveryHour = true;
        nextErrors.deliveryHour = reqLabel(t("fields.deliveryHour"));
      }
    }

    // ADVANCED: optional. Only validate JSON if textarea has content
    if (showAdvanced) {
      const hasPickupList = String(pickupListJson ?? "").trim().length > 0;
      if (hasPickupList) {
        try {
          const arr = JSON.parse(pickupListJson);
          if (!Array.isArray(arr)) {
            nextInvalid.pickupList = true;
            nextErrors.pickupList = reqLabel(t("fields.pickupList"));
          }
        } catch {
          nextInvalid.pickupList = true;
          nextErrors.pickupList = reqLabel(t("fields.pickupList"));
        }
      }

      const hasDeliveryList = String(deliveryListJson ?? "").trim().length > 0;
      if (hasDeliveryList) {
        try {
          const arr = JSON.parse(deliveryListJson);
          if (!Array.isArray(arr)) {
            nextInvalid.deliveryList = true;
            nextErrors.deliveryList = reqLabel(t("fields.deliveryList"));
          }
        } catch {
          nextInvalid.deliveryList = true;
          nextErrors.deliveryList = reqLabel(t("fields.deliveryList"));
        }
      }
    }

    setInvalidFields(nextInvalid);
    setFieldErrors(nextErrors);

    if (Object.keys(nextInvalid).length > 0) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!validate()) return;

    setLoading(true);
    try {
      let net_payment_days_carrier = null;
      if (netDaysEffective !== null) {
        net_payment_days_carrier = netDaysEffective;
      } else {
        const d = diffDays(issueDate, paymentDueDate);
        if (d !== null && d >= 0) net_payment_days_carrier = d;
      }

      const payload = {
        purchaseOrderNumber: purchaseOrderNumber ? Number(purchaseOrderNumber) : undefined,

        status,
        transportsessionId: transportsessionId ? Number(transportsessionId) : null,
        loadId: loadId ? Number(loadId) : null,

        client: client || null,

        // optional general date/hour
        date: date || null,
        hour: hour || null,

        pickup: pickup || null,
        delivery: delivery || null,

        price: price === "" ? null : Number(String(price).replace(",", ".")),
        price4subcontrated:
          price4subcontrated === "" ? null : Number(String(price4subcontrated).replace(",", ".")),

        vatRate: vatRate === "" ? null : Number(String(vatRate).replace(",", ".")),
        issuerCountry: issuerCountry ? issuerCountry.toUpperCase() : null,

        issueDate: issueDate || null,
        paymentDueDate: paymentDueDate || null,

        pickupDate: pickupDate || null,
        pickupHour: pickupHour || null,
        deliveryDate: deliveryDate || null,
        deliveryHour: deliveryHour || null,

        net_payment_days_carrier,

        // advanced (optional)
        pickupCount: Number(pickupCount || 0),
        deliveryCount: Number(deliveryCount || 0),
        pickupLocation: pickupLocation || null,
        deliveryLocation: deliveryLocation || null,
        pickupList: showAdvanced ? JSON.parse(pickupListJson || "[]") : undefined,
        deliveryList: showAdvanced ? JSON.parse(deliveryListJson || "[]") : undefined,
      };

      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      const res = await axios.post(CREATE_PO_ENDPOINT, payload);

      const created = res?.data?.purchaseOrder ?? res?.data ?? null;
      const poNum = created?.purchaseOrderNumber ?? res?.data?.purchaseOrderNumber ?? purchaseOrderNumber;

      setCreatedPoNumber(poNum ? String(poNum) : "");
      setErrorMessage("");
      setShowSuccessPopup(true);

      setTimeout(() => {
        navigate("/purchaseorders");
      }, 1200);
    } catch (err) {
      console.error("CreatePurchaseOrders error:", err);
      const rawError = err?.response?.data?.error;
      const errorKey = err?.response?.data?.errorKey;

      setShowSuccessPopup(false);

      if (errorKey) {
        setErrorMessage(t(errorKey));
      } else if (rawError) {
        setErrorMessage(t("messages.failedWithReason", { reason: String(rawError) }));
      } else {
        setErrorMessage(t("messages.failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-purchase-orders-page">
      <div
        className="create-purchase-orders-back-arrow"
        onClick={() => navigate("/purchaseorders")}
      >
        ← {t("page.back")}
      </div>

      <div className="create-purchase-orders-header">
        <h2 className="create-purchase-orders-title">{t("page.title")}</h2>
      </div>

      {errorMessage && (
        <p className="create-purchase-orders-error-message">{errorMessage}</p>
      )}

      {showSuccessPopup && (
        <div className="create-purchase-orders-successPopup-overlay">
          <div className="create-purchase-orders-successPopup">
            <div className="create-purchase-orders-successPopup-icon">✓</div>

            <h3>
              {t("success.createdTitle", {
                defaultValue: "Ordem de Compra criada",
              })}
            </h3>

            <p>
              {t("success.created", {
                number: createdPoNumber,
                defaultValue: createdPoNumber
                  ? `Ordem de Compra criada com sucesso #${createdPoNumber}.`
                  : "Ordem de Compra criada com sucesso.",
              })}
            </p>

            <button
              type="button"
              onClick={() => navigate("/purchaseorders")}
              className="create-purchase-orders-successPopup-btn"
            >
              {t("actions.ok", {
                defaultValue: "OK",
              })}
            </button>
          </div>
        </div>
      )}
      
      <div className="create-purchase-orders-card">
        <form onSubmit={handleSubmit} className="create-purchase-orders-form">
          {/* ROW 1 — PO / Status / Client */}
          <div className="create-purchase-orders-row">
            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.purchaseOrderNumber")}</label>
              <input
                type="text"
                value={purchaseOrderNumber}
                readOnly
                placeholder={t("placeholders.auto")}
                className="create-purchase-orders-input"
              />
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.status")}*</label>

              <div
                className={`create-purchase-orders-select-wrap ${invalidFields.status ? "has-error" : ""}`}
              >
                <Select
                  classNamePrefix="react-select"
                  styles={customSelectStyles}
                  options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find((o) => o.value === status) || null}
                  onChange={(opt) => {
                    const v = opt?.value ?? "";
                    setStatus(v);
                    const bad = !String(v).trim();
                    markInvalid("status", bad);
                    if (!bad) clearFieldError("status");
                  }}
                  isClearable
                  placeholder={t("placeholders.status")}
                />
              </div>

              {fieldErrors.status && (
                <div className="create-purchase-orders-field-error">{fieldErrors.status}</div>
              )}
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.client")}*</label>
              <input
                type="text"
                value={client}
                onChange={(e) => {
                  const v = e.target.value;
                  setClient(v);

                  const bad = !v.trim();
                  markInvalid("client", bad);
                  if (!bad) clearFieldError("client");
                }}
                className={`create-purchase-orders-input ${
                  invalidFields.client ? "create-purchase-orders-input-error" : ""
                }`}
                placeholder={t("placeholders.client")}
              />
              {fieldErrors.client && (
                <div className="create-purchase-orders-field-error">{fieldErrors.client}</div>
              )}
            </div>
          </div>

          {/* ROW 2 — Issuer / VAT / Price */}
          <div className="create-purchase-orders-row">
            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.issuerCountry")}*</label>
              <input
                type="text"
                value={issuerCountry}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase().slice(0, 2);
                  setIssuerCountry(v);

                  const bad = !v.trim() || !isValidISO2(v);
                  markInvalid("issuerCountry", bad);
                  if (!bad) clearFieldError("issuerCountry");
                }}
                className={`create-purchase-orders-input ${
                  invalidFields.issuerCountry ? "create-purchase-orders-input-error" : ""
                }`}
                placeholder={t("placeholders.issuerCountry")}
              />
              {fieldErrors.issuerCountry && (
                <div className="create-purchase-orders-field-error">{fieldErrors.issuerCountry}</div>
              )}
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.vatRate")}*</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={vatRate}
                onChange={(e) => {
                  const v = e.target.value;
                  setVatRate(v);

                  const n = Number(String(v).replace(",", "."));
                  const bad = !String(v).trim() || !Number.isFinite(n) || n < 0 || n > 100;
                  markInvalid("vatRate", bad);
                  if (!bad) clearFieldError("vatRate");
                }}
                className={`create-purchase-orders-input ${
                  invalidFields.vatRate ? "create-purchase-orders-input-error" : ""
                }`}
                placeholder={t("placeholders.vatRate")}
              />
              {fieldErrors.vatRate && (
                <div className="create-purchase-orders-field-error">{fieldErrors.vatRate}</div>
              )}
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.price")}*</label>
              <input
                type="text"
                value={price}
                onChange={(e) => {
                  const v = clampDecimal(e.target.value);
                  setPrice(v);

                  const n = Number(String(v).replace(",", "."));
                  const bad = !String(v).trim() || !Number.isFinite(n) || n < 0;
                  markInvalid("price", bad);
                  if (!bad) clearFieldError("price");
                }}
                className={`create-purchase-orders-input ${
                  invalidFields.price ? "create-purchase-orders-input-error" : ""
                }`}
                placeholder={t("placeholders.price")}
              />
              {fieldErrors.price && (
                <div className="create-purchase-orders-field-error">{fieldErrors.price}</div>
              )}
            </div>
          </div>

          {/* ROW 3 — Price4Sub / IssueDate / PaymentTerms */}
          <div className="create-purchase-orders-row">
            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.price4subcontrated")}*</label>
              <input
                type="text"
                value={price4subcontrated}
                onChange={(e) => {
                  const v = clampDecimal(e.target.value);
                  setPrice4subcontrated(v);

                  const n = Number(String(v).replace(",", "."));
                  const bad = !String(v).trim() || !Number.isFinite(n) || n < 0;
                  markInvalid("price4subcontrated", bad);
                  if (!bad) clearFieldError("price4subcontrated");
                }}
                className={`create-purchase-orders-input ${
                  invalidFields.price4subcontrated ? "create-purchase-orders-input-error" : ""
                }`}
                placeholder={t("placeholders.price")}
              />
              {fieldErrors.price4subcontrated && (
                <div className="create-purchase-orders-field-error">
                  {fieldErrors.price4subcontrated}
                </div>
              )}
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.issueDate")}*</label>
              <div className="create-purchase-orders-input-with-icon">
                <FaCalendarAlt className="create-purchase-orders-icon" />
                <input
                  type="date"
                  value={issueDate}
                  onClick={openNativeDatePicker}
                  onFocus={openNativeDatePicker}
                  onChange={(e) => {
                    const v = e.target.value;
                    setIssueDate(v);

                    const bad = !v;
                    markInvalid("issueDate", bad);
                    if (!bad) clearFieldError("issueDate");
                  }}
                  className={`create-purchase-orders-input ${
                    invalidFields.issueDate ? "create-purchase-orders-input-error" : ""
                  }`}
                />
              </div>
              {fieldErrors.issueDate && (
                <div className="create-purchase-orders-field-error">{fieldErrors.issueDate}</div>
              )}
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.paymentTerms")}*</label>

              <div
                className={`create-purchase-orders-select-wrap ${invalidFields.netDays ? "has-error" : ""}`}
              >
                <Select
                  classNamePrefix="react-select"
                  styles={customSelectStyles}
                  options={[...NET_DAYS_OPTIONS, { value: "CUSTOM", label: t("netDays.custom") }]}
                  value={
                    netDays === "CUSTOM"
                      ? { value: "CUSTOM", label: t("netDays.custom") }
                      : NET_DAYS_OPTIONS.find((o) => o.value === netDays) || null
                  }
                  onChange={(opt) => {
                    const v = opt?.value ?? null; // X -> null
                    setNetDays(v);
                    const bad = v === null || v === "";
                    markInvalid("netDays", bad);
                    if (!bad) clearFieldError("netDays");
                    if (v !== "CUSTOM") {
                      setNetDaysCustom("");
                      markInvalid("netDaysCustom", false);
                      clearFieldError("netDaysCustom");
                    }
                  }}
                  isClearable
                  placeholder={t("placeholders.paymentTerms")}
                />
              </div>

              {fieldErrors.netDays && (
                <div className="create-purchase-orders-field-error">{fieldErrors.netDays}</div>
              )}

              {netDays === "CUSTOM" && (
                <>
                  <input
                    type="number"
                    min="0"
                    value={netDaysCustom}
                    onChange={(e) => {
                      const v = e.target.value;
                      setNetDaysCustom(v);

                      const bad = !String(v ?? "").trim();
                      markInvalid("netDaysCustom", bad);
                      if (!bad) clearFieldError("netDaysCustom");
                    }}
                    className={`create-purchase-orders-input create-purchase-orders-mt-8 ${
                      invalidFields.netDaysCustom ? "create-purchase-orders-input-error" : ""
                    }`}
                    placeholder={t("placeholders.netDays")}
                  />

                  {fieldErrors.netDaysCustom && (
                    <div className="create-purchase-orders-field-error">{fieldErrors.netDaysCustom}</div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ROW 4 — DueDate / Optional Date / Optional Hour */}
          <div className="create-purchase-orders-row">
            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.paymentDueDate")}*</label>
              <div className="create-purchase-orders-input-with-icon">
                <FaCalendarAlt className="create-purchase-orders-icon" />
                <input
                  type="date"
                  value={paymentDueDate}
                  onClick={openNativeDatePicker}
                  onFocus={openNativeDatePicker}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPaymentDueDate(v);

                    const bad = !v;
                    markInvalid("paymentDueDate", bad);
                    if (!bad) clearFieldError("paymentDueDate");
                  }}
                  className={`create-purchase-orders-input ${
                    invalidFields.paymentDueDate ? "create-purchase-orders-input-error" : ""
                  }`}
                />
              </div>
              {fieldErrors.paymentDueDate && (
                <div className="create-purchase-orders-field-error">{fieldErrors.paymentDueDate}</div>
              )}
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.dateOptional")}</label>
              <div className="create-purchase-orders-input-with-icon">
                <FaCalendarAlt className="create-purchase-orders-icon" />
                <input
                  type="date"
                  value={date}
                  onClick={openNativeDatePicker}
                  onFocus={openNativeDatePicker}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDate(v);
                    // optional -> no invalid state just for being empty
                    markInvalid("date", false);
                    clearFieldError("date");
                  }}
                  className="create-purchase-orders-input"
                />
              </div>
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.hourOptional")}</label>
              <div className="create-purchase-orders-input-with-icon">
                <FaClock className="create-purchase-orders-icon" />
                <input
                  type="text"
                  value={hour}
                  onChange={(e) => {
                    const v = normalizeHour(e.target.value);
                    setHour(v);

                    // optional: only invalid if filled AND invalid
                    const bad = String(v).trim() !== "" && !isValidHour(v);
                    markInvalid("hour", bad);
                    if (!bad) clearFieldError("hour");
                  }}
                  className={`create-purchase-orders-input ${
                    invalidFields.hour ? "create-purchase-orders-input-error" : ""
                  }`}
                  placeholder={t("placeholders.hour")}
                />
              </div>
              {fieldErrors.hour && (
                <div className="create-purchase-orders-field-error">{fieldErrors.hour}</div>
              )}
            </div>
          </div>

          {/* ROW 5 — Pickup / Pickup Date / Pickup Hour */}
          <div className="create-purchase-orders-row">
            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.pickup")}*</label>
              <input
                type="text"
                value={pickup}
                onChange={(e) => {
                  const v = e.target.value;
                  setPickup(v);

                  const bad = !String(v ?? "").trim();
                  markInvalid("pickup", bad);
                  if (!bad) clearFieldError("pickup");
                }}
                className={`create-purchase-orders-input ${
                  invalidFields.pickup ? "create-purchase-orders-input-error" : ""
                }`}
                placeholder={t("placeholders.pickup")}
              />
              {fieldErrors.pickup && (
                <div className="create-purchase-orders-field-error">{fieldErrors.pickup}</div>
              )}
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.pickupDate")}*</label>
              <div className="create-purchase-orders-input-with-icon">
                <FaCalendarAlt className="create-purchase-orders-icon" />
                <input
                  type="date"
                  value={pickupDate}
                  onClick={openNativeDatePicker}
                  onFocus={openNativeDatePicker}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPickupDate(v);

                    const bad = !v;
                    markInvalid("pickupDate", bad);
                    if (!bad) clearFieldError("pickupDate");
                  }}
                  className={`create-purchase-orders-input ${
                    invalidFields.pickupDate ? "create-purchase-orders-input-error" : ""
                  }`}
                />
              </div>
              {fieldErrors.pickupDate && (
                <div className="create-purchase-orders-field-error">{fieldErrors.pickupDate}</div>
              )}
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.pickupHour")}*</label>
              <div className="create-purchase-orders-input-with-icon">
                <FaClock className="create-purchase-orders-icon" />
                <input
                  type="text"
                  value={pickupHour}
                  onChange={(e) => {
                    const v = normalizeHour(e.target.value);
                    setPickupHour(v);

                    const bad = !String(v).trim() || !isValidHour(v);
                    markInvalid("pickupHour", bad);
                    if (!bad) clearFieldError("pickupHour");
                  }}
                  className={`create-purchase-orders-input ${
                    invalidFields.pickupHour ? "create-purchase-orders-input-error" : ""
                  }`}
                  placeholder={t("placeholders.hour")}
                />
              </div>
              {fieldErrors.pickupHour && (
                <div className="create-purchase-orders-field-error">{fieldErrors.pickupHour}</div>
              )}
            </div>
          </div>

          {/* ROW 6 — Delivery / Delivery Date / Delivery Hour */}
          <div className="create-purchase-orders-row">
            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.delivery")}*</label>
              <input
                type="text"
                value={delivery}
                onChange={(e) => {
                  const v = e.target.value;
                  setDelivery(v);

                  const bad = !String(v ?? "").trim();
                  markInvalid("delivery", bad);
                  if (!bad) clearFieldError("delivery");
                }}
                className={`create-purchase-orders-input ${
                  invalidFields.delivery ? "create-purchase-orders-input-error" : ""
                }`}
                placeholder={t("placeholders.delivery")}
              />
              {fieldErrors.delivery && (
                <div className="create-purchase-orders-field-error">{fieldErrors.delivery}</div>
              )}
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.deliveryDate")}*</label>
              <div className="create-purchase-orders-input-with-icon">
                <FaCalendarAlt className="create-purchase-orders-icon" />
                <input
                  type="date"
                  value={deliveryDate}
                  onClick={openNativeDatePicker}
                  onFocus={openNativeDatePicker}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDeliveryDate(v);

                    const bad = !v;
                    markInvalid("deliveryDate", bad);
                    if (!bad) clearFieldError("deliveryDate");
                  }}
                  className={`create-purchase-orders-input ${
                    invalidFields.deliveryDate ? "create-purchase-orders-input-error" : ""
                  }`}
                />
              </div>
              {fieldErrors.deliveryDate && (
                <div className="create-purchase-orders-field-error">{fieldErrors.deliveryDate}</div>
              )}
            </div>

            <div className="create-purchase-orders-field">
              <label className="create-purchase-orders-label">{t("fields.deliveryHour")}*</label>
              <div className="create-purchase-orders-input-with-icon">
                <FaClock className="create-purchase-orders-icon" />
                <input
                  type="text"
                  value={deliveryHour}
                  onChange={(e) => {
                    const v = normalizeHour(e.target.value);
                    setDeliveryHour(v);

                    const bad = !String(v).trim() || !isValidHour(v);
                    markInvalid("deliveryHour", bad);
                    if (!bad) clearFieldError("deliveryHour");
                  }}
                  className={`create-purchase-orders-input ${
                    invalidFields.deliveryHour ? "create-purchase-orders-input-error" : ""
                  }`}
                  placeholder={t("placeholders.hour")}
                />
              </div>
              {fieldErrors.deliveryHour && (
                <div className="create-purchase-orders-field-error">{fieldErrors.deliveryHour}</div>
              )}
            </div>
          </div>

          {/* ADVANCED TOGGLE */}
          <div className="create-purchase-orders-advanced-toggle-row">
            <button
              type="button"
              className="create-purchase-orders-secondary-btn"
              onClick={() => setShowAdvanced((s) => !s)}
            >
              {showAdvanced ? t("actions.hideAdvanced") : t("actions.showAdvanced")}
            </button>

            <div className="create-purchase-orders-advanced-hint">{t("advanced.hint")}</div>
          </div>

          {/* ADVANCED SECTION */}
          {showAdvanced && (
            <div className="create-purchase-orders-advanced">
              {/* PICKUP — optional */}
              <div className="create-purchase-orders-row create-purchase-orders-row-pd">
                <div className="create-purchase-orders-field">
                  <label className="create-purchase-orders-label">{t("fields.pickupCount")}</label>
                  <input
                    type="number"
                    min="0"
                    value={pickupCount}
                    onChange={(e) => setPickupCount(e.target.value)}
                    className="create-purchase-orders-input"
                  />
                </div>

                <div className="create-purchase-orders-field">
                  <label className="create-purchase-orders-label">{t("fields.pickupLocation")}</label>
                  <input
                    type="text"
                    value={pickupLocation}
                    onChange={(e) => setPickupLocation(e.target.value)}
                    className="create-purchase-orders-input"
                  />
                </div>

                <div className="create-purchase-orders-field">
                  <label className="create-purchase-orders-label">{t("fields.pickupList")}</label>
                  <textarea
                    rows={1}
                    value={pickupListJson}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPickupListJson(v);

                      const has = String(v ?? "").trim().length > 0;
                      if (!has) {
                        markInvalid("pickupList", false);
                        clearFieldError("pickupList");
                        return;
                      }

                      try {
                        const arr = JSON.parse(v);
                        const bad = !Array.isArray(arr);
                        markInvalid("pickupList", bad);
                        if (!bad) clearFieldError("pickupList");
                      } catch {
                        markInvalid("pickupList", true);
                      }
                    }}
                    className={`create-purchase-orders-input create-purchase-orders-textarea-compact ${
                      invalidFields.pickupList ? "create-purchase-orders-input-error" : ""
                    }`}
                    placeholder={t("placeholders.pickupList")}
                  />
                  {fieldErrors.pickupList && (
                    <div className="create-purchase-orders-field-error">{fieldErrors.pickupList}</div>
                  )}
                </div>
              </div>

              {/* DELIVERY — optional */}
              <div className="create-purchase-orders-row create-purchase-orders-row-pd">
                <div className="create-purchase-orders-field">
                  <label className="create-purchase-orders-label">{t("fields.deliveryCount")}</label>
                  <input
                    type="number"
                    min="0"
                    value={deliveryCount}
                    onChange={(e) => setDeliveryCount(e.target.value)}
                    className="create-purchase-orders-input"
                  />
                </div>

                <div className="create-purchase-orders-field">
                  <label className="create-purchase-orders-label">{t("fields.deliveryLocation")}</label>
                  <input
                    type="text"
                    value={deliveryLocation}
                    onChange={(e) => setDeliveryLocation(e.target.value)}
                    className="create-purchase-orders-input"
                  />
                </div>

                <div className="create-purchase-orders-field">
                  <label className="create-purchase-orders-label">{t("fields.deliveryList")}</label>
                  <textarea
                    rows={1}
                    value={deliveryListJson}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDeliveryListJson(v);

                      const has = String(v ?? "").trim().length > 0;
                      if (!has) {
                        markInvalid("deliveryList", false);
                        clearFieldError("deliveryList");
                        return;
                      }

                      try {
                        const arr = JSON.parse(v);
                        const bad = !Array.isArray(arr);
                        markInvalid("deliveryList", bad);
                        if (!bad) clearFieldError("deliveryList");
                      } catch {
                        markInvalid("deliveryList", true);
                      }
                    }}
                    className={`create-purchase-orders-input create-purchase-orders-textarea-compact ${
                      invalidFields.deliveryList ? "create-purchase-orders-input-error" : ""
                    }`}
                    placeholder={t("placeholders.deliveryList")}
                  />
                  {fieldErrors.deliveryList && (
                    <div className="create-purchase-orders-field-error">{fieldErrors.deliveryList}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ACTIONS */}
          <div className="create-purchase-orders-actions">
            <button
              type="button"
              className="create-purchase-orders-secondary-btn"
              onClick={() => navigate(-1)}
            >
              {t("actions.cancel")}
            </button>

            <button type="submit" className="create-purchase-orders-primary-btn" disabled={loading}>
              {loading ? t("actions.saving") : t("actions.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePurchaseOrders;
